import React, { useEffect, useRef, useMemo } from 'react';
import { useThemeTokens } from '../../theme/ThemeProvider';

// Placeholder para plotly.js - se instalará después
// import Plotly from 'plotly.js-dist-min';

const Chart = ({ 
  data = [],
  layout = {},
  config = {},
  type = 'line',
  title,
  xTitle,
  yTitle,
  theme: chartTheme = 'auto',
  responsive = true,
  loading = false,
  height = 400,
  className = '',
  style = {},
  onPlotlyClick,
  onPlotlyHover,
  onPlotlySelect,
  ...props 
}) => {
  const { getColor, typography } = useThemeTokens();
  const plotRef = useRef(null);
  const chartRef = useRef(null);
  
  // Configuración de tema basada en design system
  const getThemeLayout = () => {
    const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
    const paper = isDark ? getComputedStyle(document.documentElement).getPropertyValue('--color-neutral-lightGray') || getColor('neutral.white') : getColor('neutral.white');
    const text = isDark ? getComputedStyle(document.documentElement).getPropertyValue('--color-neutral-darkGray') || getColor('neutral.darkGray') : getColor('neutral.darkGray');
    const grid = isDark ? getComputedStyle(document.documentElement).getPropertyValue('--color-neutral-mediumGray') || getColor('neutral.lightGray') : getColor('neutral.lightGray');
    const legendBg = isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.8)';
    return {
      autosize: true,
      font: {
        family: typography.fontFamilies.primary,
        size: 12,
        color: text,
      },
      paper_bgcolor: paper,
      plot_bgcolor: paper,
      colorway: [
        getColor('primary.blue'),
        getColor('accent.green'),
        getColor('accent.orange'),
        getColor('primary.darkBlue'),
        getColor('primary.lightBlue'),
        getColor('neutral.mediumGray'),
      ],
      title: {
        font: {
          size: 16,
          color: text,
          family: typography.fontFamilies.primary,
        },
        x: 0.5,
        xanchor: 'center',
      },
      xaxis: {
        gridcolor: grid,
        linecolor: grid,
        tickcolor: grid,
        title: { font: { color: text } }
      },
      yaxis: {
        gridcolor: grid,
        linecolor: grid,
        tickcolor: grid,
        title: { font: { color: text } }
      },
      legend: {
        bgcolor: legendBg,
        bordercolor: grid,
        borderwidth: 1,
      },
      margin: { l: 60, r: 40, b: 50, t: 60, pad: 4 }
    };
  };
  
  // Configuración por defecto
  const defaultConfig = {
    responsive: true,
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
    locale: 'es',
    ...config
  };
  
  // Layout combinado
  const combinedLayout = useMemo(() => {
    const themeLayout = getThemeLayout();
    
    const finalLayout = {
      ...themeLayout,
      ...layout,
      height: height,
    };
    
    if (title) {
      finalLayout.title = {
        ...finalLayout.title,
        text: title,
      };
    }
    
    if (xTitle) {
      finalLayout.xaxis = {
        ...finalLayout.xaxis,
        title: xTitle,
      };
    }
    
    if (yTitle) {
      finalLayout.yaxis = {
        ...finalLayout.yaxis,
        title: yTitle,
      };
    }
    
    return finalLayout;
  }, [layout, title, xTitle, yTitle, height, getColor, typography]);
  
  // Función placeholder para crear gráfico básico sin plotly
  const createBasicChart = () => {
    if (!chartRef.current) return;
    
    // Crear un gráfico SVG básico como fallback
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', height);
    svg.style.backgroundColor = getColor('neutral.white');
    svg.style.border = `1px solid ${getColor('neutral.lightGray')}`;
    svg.style.borderRadius = '8px';
    
    // Añadir texto de placeholder
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', '50%');
    text.setAttribute('y', '50%');
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dy', '0.35em');
    text.style.fontSize = '14px';
    text.style.fill = getColor('neutral.mediumGray');
    text.style.fontFamily = typography.fontFamilies.primary;
    text.textContent = 'Gráfico placeholder - Instalar plotly.js para visualizaciones completas';
    
    svg.appendChild(text);
    
    // Limpiar contenido anterior y añadir SVG
    chartRef.current.innerHTML = '';
    chartRef.current.appendChild(svg);
  };
  
  useEffect(() => {
    // Verificar si Plotly está disponible
    if (typeof window !== 'undefined' && window.Plotly) {
      // Usar Plotly si está disponible
      const Plotly = window.Plotly;
      
      if (chartRef.current) {
        Plotly.newPlot(chartRef.current, data, combinedLayout, defaultConfig)
          .then(() => {
            plotRef.current = chartRef.current;
            
            // Añadir event listeners
            if (onPlotlyClick) {
              chartRef.current.on('plotly_click', onPlotlyClick);
            }
            if (onPlotlyHover) {
              chartRef.current.on('plotly_hover', onPlotlyHover);
            }
            if (onPlotlySelect) {
              chartRef.current.on('plotly_selected', onPlotlySelect);
            }
          })
          .catch(err => {
            console.warn('Error creando gráfico Plotly:', err);
            createBasicChart();
          });
      }
    } else {
      // Fallback a gráfico básico
      createBasicChart();
    }
    
    return () => {
      if (plotRef.current && window.Plotly) {
        window.Plotly.purge(plotRef.current);
      }
    };
  }, [data, combinedLayout, defaultConfig]);
  
  const containerStyles = {
    width: '100%',
    height: height,
    position: 'relative',
    backgroundColor: 'transparent',
    borderRadius: '8px',
    overflow: 'hidden',
    ...style,
  };
  
  const loadingStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: getColor('neutral.white'),
    zIndex: 10,
  };
  
  if (loading) {
    return (
      <div className={`gaero-chart ${className}`} style={containerStyles} {...props}>
        <div style={loadingStyles}>
          <span className="loading-spinner" />
          <span style={{ marginLeft: '8px', color: getColor('neutral.mediumGray') }}>
            Cargando gráfico...
          </span>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`gaero-chart ${className}`} style={containerStyles} {...props}>
      <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

// Funciones helper para crear datos de Plotly comunes
export const createLineChart = (x, y, name = 'Serie', color) => ({
  x,
  y,
  type: 'scatter',
  mode: 'lines+markers',
  name,
  line: color ? { color } : undefined,
});

export const createBarChart = (x, y, name = 'Serie', color) => ({
  x,
  y,
  type: 'bar',
  name,
  marker: color ? { color } : undefined,
});

export const createPieChart = (labels, values, name = 'Serie') => ({
  labels,
  values,
  type: 'pie',
  name,
});

export const createScatterChart = (x, y, name = 'Serie', color) => ({
  x,
  y,
  mode: 'markers',
  type: 'scatter',
  name,
  marker: color ? { color } : undefined,
});

export const createHistogram = (x, name = 'Histograma', bins = 20) => ({
  x,
  type: 'histogram',
  name,
  nbinsx: bins,
});

export default Chart;
